import * as React from "react";
import { Button, notification, Tag, Tooltip } from "antd";
import { FormListFieldLabels } from "../../../gvc/formListFieldLabels";
import { EditOutlined, DownloadOutlined, PlusCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { EnvVarsEditor } from "./envVarsEditor";
import { envInputToText } from "../utils/envVar";
import { FormListFieldItem } from "../../../gvc/formListFieldItem";
import { v4 as uuidv4 } from "uuid";
import { EnvVarItem } from "./envVarItem";
import { formSet, formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { useAppSelector } from "../../../../store/hooks";
import { selectOrg } from "../../../../store/contextSlice";
import { store } from "../../../../store";
import { cloneDeep, get } from "lodash";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { EnvVarInput } from "../../../../schema/types/workload/input";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { getDefaultKindSelectState } from "../../../../components/antd/kindselect/KindSelect";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { ImportButton, envVarTransformer } from "../../../../components/import";
import { useRevealEnvSecrets } from "../../../../reactHooks/useRevealEnvSecrets";

interface Props {
  index: number;
}
export const WorkloadContainerEnvVars: React.FC<Props> = React.memo(({ index }) => {
  const { id } = useCpFormContext();
  const drawer = useDrawerContext();
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);

  const org = useAppSelector(selectOrg);

  const name = useCpFormValue(`name`);
  const gvc = useCpFormValue(`gvc`);
  const gvcName = React.useMemo(() => {
    const parsedLink = parseLink(gvc.link, { useInputCtx: true });
    return parsedLink.name;
  }, [gvc.link]);
  const image = useCpFormValue(`containers.${index}.image`);
  const env: EnvVarInput[] = useCpFormValue(`containers.${index}.env`);
  const { revealState, handleRevealAll, handleHideAll } = useRevealEnvSecrets(env);
  const hasSecretEnvVars = React.useMemo(
    () => env.some((e) => e.prefix === "cpln://secret/" && e.cplnSecret.value),
    [env],
  );

  const defaultVars = React.useMemo(() => {
    return [
      {
        name: "CPLN_NAME",
        value: name || "@runtime",
      },
      {
        name: "CPLN_GLOBAL_ENDPOINT", // full url, but the given domain can be different for each user or workload in prod
        value: "@runtime",
      },
      {
        name: "CPLN_GVC",
        value: gvcName || "@runtime",
      },
      {
        name: "CPLN_GVC_ALIAS",
        value: gvc.item?.alias || "@runtime",
      },
      {
        name: "CPLN_LOCATION", //*
        value: "@runtime",
      },
      {
        name: "CPLN_PROVIDER", //*
        value: "@runtime",
      },
      {
        name: "CPLN_ORG", // org link
        value: org || "@runtime",
      },
      {
        name: "CPLN_WORKLOAD", // workload link
        value: org && gvc.value && name ? `/org/${org}/gvc/${gvc.value}/workload/${name}` : "@runtime",
      },
      {
        name: "CPLN_WORKLOAD_VERSION",
        value: "@runtime",
      },
      {
        name: "CPLN_TOKEN",
        value: "@runtime",
      },
      {
        name: "CPLN_IMAGE",
        value: image.isExternal ? image.externalValue : image.cplnValue.value || "@runtime",
      },
    ];
  }, [org, name, image, gvc]);

  async function handleExport(): Promise<void> {
    try {
      const form = store.getState().form.instances[id];

      const envVars = cloneDeep(get(form.values, `containers.${index}.env`)) as EnvVarInput[];
      const blob = envInputToText(envVars);
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      let filename = "";
      if (org) {
        filename += `${org}`;
      }
      if (gvcName) {
        filename += `-${gvcName}`;
      }
      if (name) {
        filename += `-${name}`;
      }
      a.download = `${filename}.env`;
      const file = new Blob([blob], { type: "text/plain" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
    } catch (e) {
      notification.warning({ message: "Failed", description: "Try again" });
    }
  }

  function add() {
    const _items = cloneDeep(env);
    _items.push({
      id: uuidv4(),
      name: "",
      prefix: "",
      text: "",
      cplnSecret: getDefaultKindSelectState(),
      k8sSecret: "",
      reference: "",
      suffix: "",
    });
    formSetValue(id, `containers.${index}.env`, _items);

    const form = store.getState().form.instances[id];
    const onBlurCounter = form.meta.onBlurCounter;
    formSet(id, `meta.onBlurCounter`, onBlurCounter + 1);
  }

  return (
    <>
      <div className="flex justify-between mb-4">
        <div className="flex items-center gap-8">
          <div className="flex items-baseline gap-2">
            <div className="flex items-center gap-2">
              <CpLabel name={`containers.${index}.inheritEnv`}>Inherit GVC Env Vars</CpLabel>
              <CpSwitch name={`containers.${index}.inheritEnv`} />
            </div>
            <span
              className="text-sm link cursor-pointer"
              onClick={() =>
                drawer.setPersistent(
                  `container-inherit-env`,
                  "Inherit GVC Environment Variables",
                  <div>
                    <div className="mb-2">
                      Appends the environment variables defined in the Parent GVC to this workload.
                    </div>
                    <div className="mb-4">You can override by using a custom variable with the same name.</div>
                    {gvc ? (
                      (gvc.item?.spec.env || []).length < 1 ? (
                        <div className="mb-4">
                          <span className="font-semibold">GVC ({gvcName})</span> has no environment variables set.
                        </div>
                      ) : null
                    ) : (
                      <div className="mb-4">Select a Parent GVC</div>
                    )}
                    {(gvc.item?.spec.env || []).length > 0 ? (
                      <>
                        <div className="mb-2 font-semibold">Parent GVC Environment Variables</div>
                        <div className="flex flex-col gap-2">
                          {(gvc.item?.spec.env || []).map((envVar) => (
                            <Tooltip
                              key={envVar.name}
                              title={
                                <span className="text-sm">
                                  {envVar.name}
                                  {envVar.value ? "=" : ""}
                                  {envVar.value}
                                </span>
                              }
                              mouseEnterDelay={`${envVar.name}${envVar.value || ""}`.length > 47 ? undefined : 9999}
                            >
                              <Tag className="text-sm flex items-center gap-1 truncate">
                                <span>{envVar.name}</span>
                                <span>{envVar.value ? "=" : ""}</span>
                                <span>{envVar.value}</span>
                              </Tag>
                            </Tooltip>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>,
                )
              }
            >
              Info
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <div className="leading-tight">Default Vars</div>
            <span
              className="text-sm link cursor-pointer"
              onClick={() =>
                drawer.setPersistent(
                  `container-default-env`,
                  "Default Environment Variables",
                  <div>
                    <div className="mb-4">
                      Each workload is injected with environment variables containing Control Plane Platform-related
                      information.
                    </div>
                    <div className="mb-2 font-semibold">Default Environment Variables</div>
                    <div className="flex flex-col gap-2">
                      {defaultVars.map((envVar) => (
                        <Tooltip
                          key={envVar.name}
                          title={
                            <span className="text-sm">
                              {envVar.name}
                              {envVar.value ? "=" : ""}
                              {envVar.value}
                            </span>
                          }
                          mouseEnterDelay={`${envVar.name} = ${envVar.value || ""}`.length > 47 ? undefined : 9999}
                        >
                          <Tag className="text-sm truncate">
                            {envVar.name}
                            {envVar.value ? "=" : ""}
                            {envVar.value}
                          </Tag>
                        </Tooltip>
                      ))}
                    </div>
                  </div>,
                )
              }
            >
              Info
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {hasSecretEnvVars ? (
            <Button
              htmlType="button"
              onClick={revealState.isRevealed ? handleHideAll : handleRevealAll}
              size="small"
              type="default"
              loading={revealState.isRevealing}
              icon={revealState.isRevealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              iconPosition="end"
            >
              {revealState.isRevealed ? "Hide Secrets" : "Reveal Secrets"}
            </Button>
          ) : null}
          <Button
            htmlType={`button`}
            onClick={() => setIsEditorOpen(true)}
            size={"small"}
            type={"default"}
            icon={<EditOutlined />}
            iconPosition="end"
          >
            Editor
          </Button>
          <ImportButton<EnvVarInput[], EnvVarInput[]>
            formId={id}
            fieldName={`containers.${index}.env`}
            actionLabel="Import environment variables"
            existingValue={env}
            transform={(parsed, existing) => envVarTransformer(parsed, existing)}
            explanation={
              <div className="text-xs">
                <div className="font-semibold mb-1">Supported formats:</div>
                <div><strong>.env</strong> — KEY=value</div>
                <div><strong>.yaml</strong> — key: value</div>
                <div><strong>.json</strong> — {`{"key": "value"}`}</div>
                <div className="mt-2 font-semibold">Special prefixes:</div>
                <div><strong>cpln://secret/</strong> — Control Plane secret</div>
                <div><strong>k8s://secret/</strong> — Kubernetes secret</div>
                <div><strong>cpln://reference/</strong> — Reference value</div>
                <div className="mt-1 opacity-75">Duplicate keys will be overwritten</div>
              </div>
            }
          />
          <Button
            htmlType={`button`}
            onClick={() => handleExport()}
            size={"small"}
            type={"default"}
            icon={<DownloadOutlined />}
            iconPosition="end"
          >
            Export
          </Button>
        </div>
      </div>
      <div>
        {env.length < 1 ? null : (
          <FormListFieldLabels
            className={`mb-1`}
            fieldLabels={[
              { label: "Name", span: 3 },
              { label: "Value", span: 1, minWidth: 160 },
              { label: "", span: 1 },
            ]}
            columnCount={8}
            hasRemoveButton
          />
        )}
        {env.map((env, envVarIndex) => {
          return (
            <FormListFieldItem columnCount={8} key={env.id} hasRemoveButton className="mb-1">
              <EnvVarItem containerIndex={index} index={envVarIndex} revealState={revealState} />
            </FormListFieldItem>
          );
        })}
        <Button type="dashed" icon={<PlusCircleOutlined />} onClick={() => add()}>
          Add Environment Variable
        </Button>
        <CpErrorWarning name={`containers.${index}.envroot`} />
      </div>
      <EnvVarsEditor index={index} isOpen={isEditorOpen} onClose={() => setIsEditorOpen(false)} />
    </>
  );
});
